import React from "react";
import { FormFeedback, Input, Label } from "reactstrap";
import Select from "react-select";
import { useTranslation } from "react-i18next";
import { ContactFieldDefinition } from "../../helpers/audience_helper";

export type CustomFieldValueMap = Record<string, string | boolean | string[] | null>;

type Option = { value: string; label: string };

type Props = {
  definitions: ContactFieldDefinition[];
  values: CustomFieldValueMap;
  onChange: (slug: string, value: string | boolean | string[] | null) => void;
  /** Erros por slug (ou genérico em `_form`). */
  errors?: Record<string, string>;
  disabled?: boolean;
  /** Em edição: avisar que campo vazio no payload apaga o valor salvo (replace-sync). */
  showReplaceHint?: boolean;
};

/**
 * Inputs tipados do catálogo custom (DOC 03 § 4.7 · LAC-079).
 * Separado do modal para não inflar ContactsList e reusar no futuro.
 */
const ContactTypedFieldInputs = ({
  definitions,
  values,
  onChange,
  errors = {},
  disabled = false,
  showReplaceHint = false,
}: Props) => {
  const { t } = useTranslation();

  if (definitions.length === 0) {
    return null;
  }

  return (
    <div className="mb-3">
      <Label className="form-label fw-medium">
        {t("contacts.fields.section")}
      </Label>
      {showReplaceHint && (
        <small className="text-muted d-block mb-2">
          {t("contacts.fields.replaceSyncHint")}
        </small>
      )}
      <div className="vstack gap-3">
        {definitions.map((def) => {
          const id = `contact-field-${def.slug}`;
          const err = errors[def.slug];
          const requiredMark = def.is_required ? " *" : "";

          if (def.type === "textarea") {
            return (
              <div key={def.slug}>
                <Label htmlFor={id}>
                  {def.label}
                  {requiredMark}
                </Label>
                <Input
                  id={id}
                  type="textarea"
                  rows={3}
                  value={String(values[def.slug] ?? "")}
                  invalid={!!err}
                  disabled={disabled}
                  required={def.is_required}
                  onChange={(e) => onChange(def.slug, e.target.value)}
                />
                {err && <FormFeedback>{err}</FormFeedback>}
              </div>
            );
          }

          if (def.type === "number") {
            return (
              <div key={def.slug}>
                <Label htmlFor={id}>
                  {def.label}
                  {requiredMark}
                </Label>
                <Input
                  id={id}
                  type="number"
                  step="any"
                  value={String(values[def.slug] ?? "")}
                  invalid={!!err}
                  disabled={disabled}
                  required={def.is_required}
                  onChange={(e) => onChange(def.slug, e.target.value)}
                />
                {err && <FormFeedback>{err}</FormFeedback>}
              </div>
            );
          }

          if (def.type === "date") {
            return (
              <div key={def.slug}>
                <Label htmlFor={id}>
                  {def.label}
                  {requiredMark}
                </Label>
                <Input
                  id={id}
                  type="date"
                  value={String(values[def.slug] ?? "")}
                  invalid={!!err}
                  disabled={disabled}
                  required={def.is_required}
                  onChange={(e) => onChange(def.slug, e.target.value)}
                />
                <small className="text-muted d-block mt-1">
                  {t("contacts.fields.dateHint")}
                </small>
                {err && <FormFeedback className="d-block">{err}</FormFeedback>}
              </div>
            );
          }

          if (def.type === "boolean") {
            const raw = values[def.slug];
            const selectValue =
              raw === true ? "true" : raw === false ? "false" : "";
            return (
              <div key={def.slug}>
                <Label htmlFor={id}>
                  {def.label}
                  {requiredMark}
                </Label>
                <Input
                  id={id}
                  type="select"
                  value={selectValue}
                  invalid={!!err}
                  disabled={disabled}
                  required={def.is_required}
                  onChange={(e) => {
                    const v = e.target.value;
                    if (v === "") {
                      onChange(def.slug, null);
                    } else {
                      onChange(def.slug, v === "true");
                    }
                  }}
                >
                  <option value="">{t("contacts.fields.booleanEmpty")}</option>
                  <option value="true">{t("contacts.fields.booleanTrue")}</option>
                  <option value="false">{t("contacts.fields.booleanFalse")}</option>
                </Input>
                {err && <FormFeedback>{err}</FormFeedback>}
              </div>
            );
          }

          if (def.type === "select") {
            const options = (def.options || []).map((opt) => ({
              value: opt,
              label: opt,
            }));
            return (
              <div key={def.slug}>
                <Label htmlFor={id}>
                  {def.label}
                  {requiredMark}
                </Label>
                <Input
                  id={id}
                  type="select"
                  value={String(values[def.slug] ?? "")}
                  invalid={!!err}
                  disabled={disabled}
                  required={def.is_required}
                  onChange={(e) => onChange(def.slug, e.target.value)}
                >
                  <option value="">{t("contacts.fields.selectEmpty")}</option>
                  {options.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </Input>
                {err && <FormFeedback>{err}</FormFeedback>}
              </div>
            );
          }

          if (def.type === "multiselect") {
            const options: Option[] = (def.options || []).map((opt) => ({
              value: opt,
              label: opt,
            }));
            const selected = Array.isArray(values[def.slug])
              ? (values[def.slug] as string[]).map((v) => ({
                  value: v,
                  label: v,
                }))
              : [];
            return (
              <div key={def.slug}>
                <Label htmlFor={id}>
                  {def.label}
                  {requiredMark}
                </Label>
                <Select
                  inputId={id}
                  classNamePrefix="select"
                  isMulti
                  isClearable
                  isDisabled={disabled}
                  options={options}
                  value={selected}
                  onChange={(opts: readonly Option[] | null) =>
                    onChange(
                      def.slug,
                      (opts || []).map((o) => o.value)
                    )
                  }
                  placeholder={t("contacts.fields.multiselectPlaceholder")}
                  noOptionsMessage={() => t("contacts.fields.noOptions")}
                />
                {err && (
                  <div className="invalid-feedback d-block">{err}</div>
                )}
              </div>
            );
          }

          // text (default)
          return (
            <div key={def.slug}>
              <Label htmlFor={id}>
                {def.label}
                {requiredMark}
              </Label>
              <Input
                id={id}
                type="text"
                value={String(values[def.slug] ?? "")}
                invalid={!!err}
                disabled={disabled}
                required={def.is_required}
                onChange={(e) => onChange(def.slug, e.target.value)}
              />
              {err && <FormFeedback>{err}</FormFeedback>}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ContactTypedFieldInputs;

/** Monta o payload `fields` para create/update (replace-sync no backend). */
export function buildFieldsPayload(
  definitions: ContactFieldDefinition[],
  values: CustomFieldValueMap
): Record<string, string | boolean | string[] | null> {
  const out: Record<string, string | boolean | string[] | null> = {};
  for (const def of definitions) {
    const raw = values[def.slug];
    if (def.type === "multiselect") {
      out[def.slug] = Array.isArray(raw) ? raw : [];
    } else if (def.type === "boolean") {
      out[def.slug] = raw === true || raw === false ? raw : null;
    } else if (raw === null || raw === undefined) {
      out[def.slug] = "";
    } else {
      out[def.slug] = String(raw);
    }
  }
  return out;
}

export function valuesFromContactFields(
  definitions: ContactFieldDefinition[],
  fields: Record<string, unknown> | null | undefined
): CustomFieldValueMap {
  const out: CustomFieldValueMap = {};
  for (const def of definitions) {
    const raw = fields?.[def.slug];
    if (def.type === "multiselect") {
      out[def.slug] = Array.isArray(raw)
        ? raw.map(String)
        : typeof raw === "string" && raw
          ? [raw]
          : [];
    } else if (def.type === "boolean") {
      out[def.slug] =
        raw === true || raw === false
          ? raw
          : raw === "true" || raw === 1 || raw === "1"
            ? true
            : raw === "false" || raw === 0 || raw === "0"
              ? false
              : null;
    } else {
      out[def.slug] = raw == null ? "" : String(raw);
    }
  }
  return out;
}
